import React, { useState, useEffect } from 'react';
import { Bars3Icon, MoonIcon, SunIcon, PlusIcon, WalletIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import TopUpModal from './modals/TopUpModal';
import { useDriver } from '../../contexts/DriverContext';

const Header = ({ toggleSidebar, className = '', showHamburger = true }) => {
  const { t } = useTranslation();
  const { driverAccount } = useDriver();
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [isPWA, setIsPWA] = useState(false);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    localStorage.setItem('darkMode', String(darkMode));
  }, [darkMode]);

  // Check URL parameters on mount to auto-open modal after Stripe redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('topup_status');

    if (status === 'success' || status === 'cancel') {
      // Auto-open modal to show result
      setShowTopUpModal(true);
    }
  }, []);

  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    setIsPWA(isStandalone);
  }, []);

  const toggleDarkMode = () => setDarkMode(prevMode => !prevMode);

  const handleTopUpSuccess = () => {
    // Modal already handles refresh, just close
    setShowTopUpModal(false);
  };

  return (
    <header
      className={`bg-slate-100 dark:bg-slate-900 z-10 w-full px-2 sm:px-8 py-2 sm:py-2 flex items-center justify-between border-b border-slate-200 dark:border-slate-700 shadow-sm ${isPWA ? 'fixed top-0 left-0 right-0' : 'flex-shrink-0'} ${className}`}
      style={{ 
       paddingTop: isPWA ? 'max(0.5rem, env(safe-area-inset-top))' : '0.5rem',
        paddingLeft: 'max(0.5rem, env(safe-area-inset-left))',
        paddingRight: 'max(0.5rem, env(safe-area-inset-right))',
      }}
    >
      <div className="flex items-center px-1 sm:px-2">
        {showHamburger && (
          <button
            onClick={toggleSidebar}
            className=" text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white focus:outline-none lg:hidden p-2"
            aria-label={t('header.toggleMenu')}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
        )}
        <h1 className="text-xl sm:text-xl font-semibold text-slate-900 dark:text-white">{t('header.appName')}</h1>
      </div>

      <div className="flex items-center space-x-2">
        {/* Balance Display */}
        <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/30 rounded-full border border-purple-200 dark:border-purple-700">
          <WalletIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
          <span className="text-sm font-semibold text-purple-700 dark:text-purple-300">
            {(driverAccount?.balance || 0).toFixed(2)} RON
          </span>
          <button
            onClick={() => setShowTopUpModal(true)}
            className="ml-1 p-1 rounded-full bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 text-white transition-all duration-200 hover:scale-110"
            aria-label={t('header.topUp')}
          >
            <PlusIcon className="h-4 w-4" />
          </button>
        </div>

        {/* Mobile Balance Button */}
        <button
          onClick={() => setShowTopUpModal(true)}
          className="sm:hidden flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-gradient-to-r from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/30 border border-purple-200 dark:border-purple-700"
          aria-label={t('header.balance')}
        >
          <WalletIcon className="h-4 w-4 text-purple-600 dark:text-purple-400" />
          <span className="text-xs font-semibold text-purple-700 dark:text-purple-300">
            {(driverAccount?.balance || 0).toFixed(2)}
          </span>
          <PlusIcon className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400" />
        </button>

        <button
          onClick={toggleDarkMode}
          className="p-2 rounded-full text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          aria-label={t('header.toggleDarkMode')}
        >
          {darkMode
            ? <SunIcon className="h-5 w-5 text-amber-500" />
            : <MoonIcon className="h-5 w-5 text-slate-700" />
          }
        </button>
      </div>

      {/* Top Up Modal */}
      <TopUpModal
        isOpen={showTopUpModal}
        onClose={() => setShowTopUpModal(false)}
        onSuccess={handleTopUpSuccess}
      />
    </header>
  );
};

export default Header;
